<template>
  <tr>
    <td class="td-compact">
      <slot name="toggleButton" />
    </td>
    <td class="td-compact">
      <slot name="editButton" />
    </td>
    <td>
      <template v-if="alarm.date">
        {{ displayDate }}
      </template>
    </td>
    <td>
      {{ time }}
    </td>
    <td>
      {{ activeDays }}
    </td>
    <td>
      <span class="">{{ timeDifference }}</span>
    </td>
    <td>
      {{ alarm.name }}
    </td>
    <td class="td-compact">
      <slot name="deleteButton" />
    </td>
  </tr>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

import { AlarmSchedule, parseServerAlarmUtc, resolveAlarmUserTimezone } from '@/entities/alarm'
import { PreferenceKey, usePreferencesStore } from '@/entities/preference'


import { useLocalization } from '@/shared/composables/localization'
import { useStoreConfiguration } from '@/shared/store/configuration'


const props = defineProps<{
  alarm: AlarmSchedule
}>()
const { t } = useLocalization()

const activeDays = computed(() => {
  let str = ''
  if (props.alarm.monday) {
    str += `${t('common.mo')}, `
  }
  if (props.alarm.tuesday) {
    str += `${t('common.tu')}, `
  }
  if (props.alarm.wednesday) {
    str += `${t('common.we')}, `
  }
  if (props.alarm.thursday) {
    str += `${t('common.th')}, `
  }
  if (props.alarm.friday) {
    str += `${t('common.fr')}, `
  }
  if (props.alarm.saturday) {
    str += `${t('common.sa')}, `
  }
  if (props.alarm.sunday) {
    str += `${t('common.su')}, `
  }

  if (str.endsWith(', ')) {
    str = str.slice(0, -2)
  }

  if (!str) {
    str = `${t('common.oneTime')}`
  }

  return str
})

const { configuration } = storeToRefs(useStoreConfiguration())
const { preferences } = storeToRefs(usePreferencesStore())
// Отображение времени будильника — в зоне пользователя.
const selectedTimezone = computed<string>(() => {
  const tz = preferences.value?.[PreferenceKey.CURRENT_TIME_ZONE]
  return resolveAlarmUserTimezone(typeof tz === 'string' ? tz : null)
})

const targetDateTime = computed(() => {
  let targetDate: string | undefined
  if (props.alarm.date) {
    targetDate = props.alarm.date
  } else {
    const weekdays = [
      [0, props.alarm.sunday],
      [1, props.alarm.monday],
      [2, props.alarm.tuesday],
      [3, props.alarm.wednesday],
      [4, props.alarm.thursday],
      [5, props.alarm.friday],
      [6, props.alarm.saturday],
    ].filter(([, isActive]) => isActive) as [number, boolean][]

    if (weekdays.length === 0) {
      return null
    }

    const now = parseServerAlarmUtc(
      dayjs().tz(selectedTimezone.value).format('YYYY-MM-DD'),
      dayjs().tz(selectedTimezone.value).format('HH:mm'),
      configuration.value.tzOffset,
    ).tz(selectedTimezone.value)
    const currentWeekday = now.day()
    const currentTime = now.format('HH:mm')

    const nextWeekday = weekdays.find(([day]) => {
      return day > currentWeekday || (day === currentWeekday && props.alarm.time > currentTime)
    }) || weekdays[0]

    const daysUntilAlarm = (nextWeekday?.[0] ?? 0) - currentWeekday
    targetDate = now.add(daysUntilAlarm < 0 ? daysUntilAlarm + 7 : daysUntilAlarm, 'day').format('YYYY-MM-DD')
  }

  return parseServerAlarmUtc(targetDate, props.alarm.time, configuration.value.tzOffset).tz(selectedTimezone.value)
})

const displayDate = computed(() => {
  if (!props.alarm.date || !targetDateTime.value) {
    return ''
  }

  return targetDateTime.value.format('DD.MM.YYYY')
})

const time = computed(() => {
  return targetDateTime.value?.format('HH:mm') ?? ''
})

const timeDifference = computed(() => {
  const currentDate = parseServerAlarmUtc(
    dayjs().tz(selectedTimezone.value).format('YYYY-MM-DD'),
    dayjs().tz(selectedTimezone.value).format('HH:mm'),
    configuration.value.tzOffset,
  ).unix()
  const isDateValid = targetDateTime.value?.isValid()

  if (!isDateValid || !targetDateTime.value) return ''

  const dt = targetDateTime.value.unix()
  const HOUR = 3600
  const DAY = HOUR * 24
  // Расчет разницы в секундах
  const diff = (dt - currentDate)
  if (diff < 0) {
    return `${t('common.theAlarmWentOff')}`
  } else {
    const days = Math.floor(diff / DAY)
    const hours = Math.floor((diff - (days * DAY)) / HOUR)
    const minutes = Math.floor((diff - (days * DAY) - (hours * HOUR)) / 60)

    let result = ''
    if (days > 0) {
      result += `${days} д.`
    }

    if (hours > 0) {
      result += ` ${hours} ч.`
    }

    if (minutes > 0) {
      result += ` ${minutes} м.`
    }

    return result
  }
})
</script>
